<template>
  <div class="bg-soft bg-header-pattern py-6 lg:py-10">
    <div class="max-w-screen-lg mx-auto lg:px-0 px-4 pb-4">
      <div class="my-14 text-center">
        <h1
          class="text-2xl lg:text-3xl font-semibold"
          :class="{ 'font-inter': locale == 'vi' }"
        >
          {{ dataLocale.data.title }}
        </h1>
        <p class="mt-4 text-base">
          {{ dataLocale.data.description }}
        </p>
      </div>
      <div class="flex items-center justify-center">
        <a
          href="https://app.qrdove.com/batch"
          class="h-12 mt-4 px-8 font-semibold bg-success text-sm sm:text-base text-white focus-visible:shadow-button-focus-success whitespace-nowrap transition inline-flex items-center justify-center rounded-full"
          rel="nofollow"
        >
          📦 {{ dataLocale.data.button }}
        </a>
      </div>
    </div>
  </div>
  <PartialsBatchContent />
  <PartialsTrackQrWidget />
  <PartialsAgencyCarousel />
</template>
<script lang="ts" setup>
import moment from "moment";
import DataLocale from "~~/config/interface/DataLocale";
import ToolLocale from "~~/config/widgets/tools-widget";
const { locale } = useI18n();
const localePath = useLocalePath();

const tools = new ToolLocale().data;
const dataLocale = new DataLocale([
  {
    locale: "en",
    title: "Generate Batch QR Codes Effortlessly",
    description:
      "Reusable batch templates streamline the process, enabling the creation of multiple QR codes in bulk. Simply input data using online forms or upload an Excel sheet for seamless import.",
    button: "Batch QR Code Generator",
  },
  {
    locale: "fr",
    title: "Générez facilement des codes QR par lots",
    description:
      "Les modèles de lots réutilisables simplifient le processus, permettant la création de plusieurs codes QR en masse. Saisissez simplement les données à l'aide de formulaires en ligne ou téléchargez une feuille Excel pour une importation transparente.",
    button: "Générateur de codes QR par lots",
  },
  {
    locale: "it",
    title: "Genera facilmente codici QR in batch",
    description:
      "I modelli batch riutilizzabili semplificano il processo, consentendo la creazione di più codici QR in blocco. Inserisci semplicemente i dati utilizzando moduli online o carica un foglio Excel per un'importazione senza interruzioni.",
    button: "Generatore di codici QR batch",
  },
  {
    locale: "de",
    title: "Generieren Sie mühelos Batch-QR-Codes",
    description:
      "Wiederverwendbare Batch-Vorlagen optimieren den Prozess und ermöglichen die Erstellung mehrerer QR-Codes in großen Mengen. Geben Sie Daten einfach über Online-Formulare ein oder laden Sie ein Excel-Blatt für den nahtlosen Import hoch.",
    button: "Batch-QR-Code-Generator",
  },
  {
    locale: "es",
    title: "Genere códigos QR por lotes sin esfuerzo",
    description:
      "Las plantillas por lotes reutilizables agilizan el proceso, lo que permite la creación de varios códigos QR en masa. Simplemente ingrese los datos usando formularios en línea o cargue una hoja de Excel para una importación sin problemas.",
    button: "Generador de códigos QR por lotes",
  },
  {
    locale: "nl",
    title: "Genereer moeiteloos batch-QR-codes",
    description:
      "Herbruikbare batchsjablonen stroomlijnen het proces en maken het mogelijk om meerdere QR-codes in bulk te maken. Voer eenvoudig gegevens in met behulp van online formulieren of upload een Excel-blad voor een naadloze import.",
    button: "Batch QR-codegenerator",
  },
  {
    locale: "pt",
    title: "Gere códigos QR em lote sem esforço",
    description:
      "Modelos de lote reutilizáveis agilizam o processo, permitindo a criação de vários códigos QR em massa. Basta inserir dados usando formulários online ou carregar uma planilha do Excel para uma importação perfeita.",
    button: "Gerador de código QR em lote",
  },
  {
    locale: "ja",
    title: "バッチQRコードを簡単に生成",
    description:
      "再利用可能なバッチテンプレートによりプロセスが合理化され、複数のQRコードをまとめて作成できます。オンラインフォームを使用してデータを入力するか、Excelシートをアップロードしてシームレスにインポートするだけです。",
    button: "バッチQRコードジェネレーター",
  },
  {
    locale: "zh",
    title: "轻松生成批量二维码",
    description:
      "可重复使用的批量模板简化了流程，支持批量创建多个二维码。只需使用在线表单输入数据或上传 Excel 表即可实现无缝导入。",
    button: "批量二维码生成器",
  },
  {
    locale: "vi",
    title: "Tạo Mã QR Hàng Loạt Dễ Dàng",
    description:
      "Các mẫu hàng loạt có thể tái sử dụng giúp hợp lý hóa quy trình, cho phép tạo nhiều mã QR hàng loạt. Chỉ cần nhập dữ liệu bằng biểu mẫu trực tuyến hoặc tải lên bảng Excel để nhập liền mạch.",
    button: "Trình tạo mã QR hàng loạt",
  },
  {
    locale: "id",
    title: "Hasilkan Kode QR Batch dengan Mudah",
    description:
      "Templat batch yang dapat digunakan kembali menyederhanakan proses, memungkinkan pembuatan banyak kode QR secara massal. Cukup masukkan data menggunakan formulir online atau unggah lembar Excel untuk impor yang lancar.",
    button: "Generator Kode QR Batch",
  },
  {
    locale: "tr",
    title: "Toplu QR Kodlarını Zahmetsizce Oluşturun",
    description:
      "Yeniden kullanılabilir toplu şablonlar, çok sayıda QR kodunun toplu olarak oluşturulmasını sağlayarak süreci kolaylaştırır. Verileri çevrimiçi formlar kullanarak girin veya sorunsuz içe aktarma için bir Excel sayfası yükleyin.",
    button: "Toplu QR Kodu Oluşturucu",
  },
  {
    locale: "hi",
    title: "बैच क्यूआर कोड आसानी से जेनरेट करें",
    description:
      "पुन: प्रयोज्य बैच टेम्प्लेट प्रक्रिया को सुव्यवस्थित करते हैं, जिससे थोक में कई क्यूआर कोड का निर्माण संभव हो पाता है। केवल ऑनलाइन फॉर्म का उपयोग करके डेटा इनपुट करें या निर्बाध आयात के लिए एक एक्सेल शीट अपलोड करें।",
    button: "बैच क्यूआर कोड जेनरेटर",
  },
  {
    locale: "th",
    title: "สร้างรหัส QR แบบแบทช์ได้อย่างง่ายดาย",
    description:
      "เทมเพลตแบบแบทช์ที่นำกลับมาใช้ใหม่ได้ช่วยให้กระบวนการคล่องตัวขึ้น ทำให้สามารถสร้างรหัส QR หลายรายการได้ในคราวเดียว เพียงป้อนข้อมูลโดยใช้แบบฟอร์มออนไลน์หรืออัปโหลดแผ่น Excel เพื่อการนำเข้าที่ราบรื่น",
    button: "ตัวสร้างรหัส QR แบบแบทช์",
  },
]);

const datajd = useDataJd();
const meta = useMeta();
const meta_tags = meta.metaTag({
  title: dataLocale.data.title,
  description: dataLocale.data.description,
  url: datajd.currentURL(),
  image: datajd.assets("/images/og/generator.jpg"),
});
const i18nHead = useLocaleHead({
  addSeoAttributes: {
    canonicalQueries: ["/"],
  },
});
useHead({
  title: dataLocale.data.title,
  meta: [
    { name: "description", content: dataLocale.data.description },
    ...(i18nHead.value.meta || []),
    ...meta_tags,
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
});

useSchemaOrg([
  defineWebPage({
    name: dataLocale.data.title,
    datePublished: moment("2023-08-28").toISOString(),
    dateModified: moment().toISOString(),
    description: dataLocale.data.description,
    inLanguage: datajd.inLanguages(true),
    url: datajd.currentURL(),
    image: datajd.assets("/images/og/generator.jpg"),
  }),
  defineBreadcrumb({
    itemListElement: [
      {
        item: {
          "@id": datajd.currentHost(),
          name: "Home",
        },
      },
      {
        item: {
          name: useI18n().t("batch"),
          "@id": datajd.currentHost() + useRoute().path,
        },
      },
    ],
  }),
]);
</script>
